import type { RegisterMutation, RegisterMutationInput } from 'generated/gql';

import { mapAdministrationToAdministrationInput } from './Administration';
import { mapAttachmentToAttachmentInput } from './Attachment';
import { mapConversionToConversionInput } from './Conversion';
import { mapDirectorToDirectorInput } from './Director';
import { mapDocumentToDocumentInput } from './Document';
import { mapEndAdministrationToEndAdministrationInput } from './EndAdministration';
import { mapEndAttachmentToEndAttachmentInput } from './EndAttachment';
import { mapEndDirectorshipToEndDirectorshipInput } from './EndDirectorship';
import { mapEndPledgeToEndPledgeInput } from './EndPledge';
import { mapEndUboToEndUboInput } from './EndUbo';
import { mapEndUsufructToEndUsufructInput } from './EndUsufruct';
import { mapIncorporationToIncorporationInput } from './Incorporation';
import { mapIssuanceToIssuanceInput } from './Issuance';
import { mapLiquidationToLiquidationInput } from './Liquidation';
import { mapOnboardingToOnboardingInput } from './Onboarding';
import { mapPayingUpToPayingUpInput } from './PayingUp';
import { mapPledgeToPledgeInput } from './Pledge';
import { mapTransferToTransferInput } from './Transfer';
import { mapUboToUboInput } from './Ubo';
import { mapUsufructToUsufructInput } from './Usufruct';
import { mapWithdrawalToWithdrawalInput } from './Withdrawal';
const mapValues =
  <Mapper extends (...args: never[]) => unknown>(mapper: Mapper) =>
  <Values extends unknown[]>(array?: [...Values]) =>
    Array.isArray(array) ? ([...array].map(mapper as never) as Array<ReturnType<Mapper>>) : null;

const mappers = {
  administration: mapValues(mapAdministrationToAdministrationInput),
  appointDirector: mapValues(mapDirectorToDirectorInput),
  attachment: mapValues(mapAttachmentToAttachmentInput),
  conversion: mapValues(mapConversionToConversionInput),
  endAdministration: mapValues(mapEndAdministrationToEndAdministrationInput),
  endAttachment: mapValues(mapEndAttachmentToEndAttachmentInput),
  endDirectorship: mapValues(mapEndDirectorshipToEndDirectorshipInput),
  endPledge: mapValues(mapEndPledgeToEndPledgeInput),
  endUbo: mapValues(mapEndUboToEndUboInput),
  endUsufruct: mapValues(mapEndUsufructToEndUsufructInput),
  incorporation: mapValues(mapIncorporationToIncorporationInput),
  issuance: mapValues(mapIssuanceToIssuanceInput),
  onboarding: mapValues(mapOnboardingToOnboardingInput),
  payingUp: mapValues(mapPayingUpToPayingUpInput),
  pledge: mapValues(mapPledgeToPledgeInput),
  transfer: mapValues(mapTransferToTransferInput),
  ubo: mapValues(mapUboToUboInput),
  usufruct: mapValues(mapUsufructToUsufructInput),
  withdrawal: mapValues(mapWithdrawalToWithdrawalInput),
  liquidation: mapLiquidationToLiquidationInput,
  document: mapDocumentToDocumentInput,
  isDraft: Boolean,
  effectiveDate: (date: string) => new Date(date),
  acknowledgedDate: (date: string) => new Date(date),
  title: String,
} as const;
type Mappers = typeof mappers;

//* similar to mapMutationToMutationInput, but with more type safety */
export const mapToUpdateMutationInput = <T extends { [k in keyof Mappers & keyof T]: T[k] }>(
  mutation: T,
) =>
  Object.fromEntries(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    Object.entries(mutation!)
      .map(([key, mutationValue]) =>
        key in mappers && mutationValue !== null
          ? [key, (mappers as any)[key](mutationValue)]
          : null,
      )
      .filter(Array.isArray),
  ) as {
    [k in keyof Mappers]: ReturnType<Mappers[k]>;
  };

export const mapMutationToMutationInput = (mutation: RegisterMutation): RegisterMutationInput => {
  const { document, liquidation, effectiveDate, acknowledgedDate, title } = mutation;
  const administration = mutation.administration
    ? mutation.administration.map(mapAdministrationToAdministrationInput)
    : null;
  const appointDirector = mutation.appointDirector
    ? mutation.appointDirector.map(mapDirectorToDirectorInput)
    : null;
  const attachment = mutation.attachment
    ? mutation.attachment.map(mapAttachmentToAttachmentInput)
    : null;
  const conversion = mutation.conversion
    ? mutation.conversion.map(mapConversionToConversionInput)
    : null;
  const endAdministration = mutation.endAdministration
    ? mutation.endAdministration.map(mapEndAdministrationToEndAdministrationInput)
    : null;
  const endAttachment = mutation.endAttachment
    ? mutation.endAttachment.map(mapEndAttachmentToEndAttachmentInput)
    : null;
  const endDirectorship = mutation.endDirectorship
    ? mutation.endDirectorship.map(mapEndDirectorshipToEndDirectorshipInput)
    : null;
  const endPledge = mutation.endPledge
    ? mutation.endPledge.map(mapEndPledgeToEndPledgeInput)
    : null;
  const endUbo = mutation.endUbo ? mutation.endUbo.map(mapEndUboToEndUboInput) : null;
  const endUsufruct = mutation.endUsufruct
    ? mutation.endUsufruct.map(mapEndUsufructToEndUsufructInput)
    : null;
  const incorporation = mutation.incorporation
    ? mutation.incorporation.map(mapIncorporationToIncorporationInput)
    : null;
  const issuance = mutation.issuance ? mutation.issuance.map(mapIssuanceToIssuanceInput) : null;
  const onboarding = mutation.onboarding
    ? mutation.onboarding.map(mapOnboardingToOnboardingInput)
    : null;
  const payingUp = mutation.payingUp ? mutation.payingUp.map(mapPayingUpToPayingUpInput) : null;
  const pledge = mutation.pledge ? mutation.pledge.map(mapPledgeToPledgeInput) : null;
  const transfer = mutation.transfer ? mutation.transfer.map(mapTransferToTransferInput) : null;
  const ubo = mutation.ubo ? mutation.ubo.map(mapUboToUboInput) : null;
  const usufruct = mutation.usufruct ? mutation.usufruct.map(mapUsufructToUsufructInput) : null;
  const withdrawal = mutation.withdrawal
    ? mutation.withdrawal.map(mapWithdrawalToWithdrawalInput)
    : null;

  return {
    isDraft: mutation.isDraft,
    dossierNumber: mutation.dossierNumber,
    document: document ? mapDocumentToDocumentInput(document) : null,
    administration,
    appointDirector,
    attachment,
    conversion,
    endAdministration,
    endAttachment,
    endDirectorship,
    endPledge,
    endUbo,
    endUsufruct,
    incorporation,
    issuance,
    liquidation,
    onboarding,
    payingUp,
    pledge,
    transfer,
    ubo,
    usufruct,
    withdrawal,
    effectiveDate,
    acknowledgedDate,
    title,
  };
};
